import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ChevronRight, Shield } from 'lucide-react';
import type { ReactNode } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

function AccordionIcon() {
    return (
        <span className="bg-muted text-muted-foreground border-border ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-transform duration-200 group-open:rotate-180">
            <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
                <path d="M5 7.5L10 12.5L15 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}

function AccordionItem({ title, children }: { title: string; children: ReactNode }) {
    return (
        <details className="group bg-card border-border overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
            <summary className="flex cursor-pointer list-none items-center justify-between px-6 py-5 select-none focus:outline-none">
                <span className="text-foreground text-sm font-bold md:text-base">{title}</span>
                <AccordionIcon />
            </summary>
            <div className="border-border/60 text-muted-foreground bg-card space-y-4 border-t px-6 pt-5 pb-6 text-sm leading-relaxed md:text-base">
                {children}
            </div>
        </details>
    );
}

export default function AcademicPolicies() {
    return (
        <LandingLayout>
            <SeoHead
                title="Academic Policies"
                description="Academic policies and administrative structure of Northeastern College."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                 
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/academic.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                     
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                     
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                     
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Academic Policies</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Academic &amp; School Policies
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Explore the administrative structures, governance councils, and academic policies of Northeastern College.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    {/* Organization and Administration */}
                    <div className="bg-card border-border relative mb-8 overflow-hidden rounded-2xl border p-6 shadow-sm md:p-10">
                        <div className="absolute top-0 left-0 h-full w-2 bg-[#0A7A35]" />
                        <h4 className="text-foreground mb-4 flex items-center gap-2 pl-2 text-lg font-bold md:text-xl">
                            <Shield className="size-5 text-[#0A7A35]" />
                            NC Organization and Administration
                        </h4>
                        <div className="text-muted-foreground space-y-4 pl-2 text-sm leading-relaxed md:text-base">
                            <p>
                                Northeastern College&#39;s organization for student development is directed, coordinated, monitored, and supervised by
                                the heads of the academic, administrative, and student councils designed to assist the individual student to attain
                                maximum self-realization and become effective in the social environment.
                            </p>
                            <p>
                                As a legal corporation, Northeastern College falls under the authority of the Board of Directors, academic and
                                administrative officers headed by the President. The Academic Council is composed of all Academic Deans, the
                                Principal, and Department Heads who assist the President on all academic matters. The Administrative Council assists
                                the President in implementing existing administrative policies and in formulating new ones.
                            </p>
                            <p className="text-foreground mt-6 text-xs font-bold tracking-wider uppercase">
                                Affiliated Organizations &amp; Associations:
                            </p>
                            <div className="text-muted-foreground grid grid-cols-1 gap-2 text-xs sm:grid-cols-2 md:grid-cols-3">
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Association of Colleges and Universities (PACU)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Private Education Retirement Annuity Association (PERAA)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Coordinating Council of Private Education (COCOPEA)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Fund for Assistance to Private Education (FAPE)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Association of Graduate Education (PAGE)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Association for Teacher Education
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Association of Geodetic Engineers
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Institute of Certified Public Accountants
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Philippine Association of Campus Student Advisers (PACSA)
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    National Association of Secretarial Educators
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Business Management Association of the Philippines
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Junior Philippine Institute of Accountants
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0A7A35]" />
                                    Private Schools Athletic Association (PRISAA)
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        <div className="space-y-4">
                            <AccordionItem title="Administrative Structure of the School">
                                <div className="space-y-4">
                                    <div>
                                        <h5 className="text-foreground mb-1 flex items-center gap-1.5 text-sm font-bold tracking-wide uppercase">
                                            Policy Making Body
                                        </h5>
                                        <h6 className="text-xs font-bold text-[#0A7A35]">Board of Directors</h6>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            The general management of the corporation is vested in the Board of Directors comprising of ten (10)
                                            members who must be stockholders in good standing; must be elected annually by the stockholders; and who
                                            must serve for one (1) year until the election and qualification of their successors.
                                        </p>
                                    </div>

                                    <div className="border-border/40 border-t pt-2">
                                        <h5 className="text-foreground mb-1 flex items-center gap-1.5 text-sm font-bold tracking-wide uppercase">
                                            The Administrators
                                        </h5>
                                        <h6 className="text-xs font-bold text-[#0A7A35]">The School President</h6>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            As the Chief Executive Officer, the President is responsible for the attainment of the mission and
                                            objectives of the school and plans, organizes, directs, and controls the institution&#39;s activities in
                                            accordance with the policies, directives and strategies set by the Board of Directors.
                                        </p>
                                    </div>

                                    <div className="border-border/40 border-t pt-2">
                                        <h6 className="text-xs font-bold text-[#0A7A35]">Vice President for Administration</h6>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            The Vice President for Administration assists the President in all administrative or
                                            administrative-related affairs of the College, advising the President, establishing standards of
                                            performance, and monitoring performance of non-teaching personnel.
                                        </p>
                                    </div>

                                    <div className="border-border/40 border-t pt-2">
                                        <h6 className="text-xs font-bold text-[#0A7A35]">Vice President for Academic Affairs</h6>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            Responsible for exercising academic leadership among colleges, deans, and faculty, coordinating with the
                                            Research Director, chairs the Academic Council, and spearheads curriculum revisions.
                                        </p>
                                    </div>

                                    <div className="border-border/40 border-t pt-2">
                                        <h6 className="text-foreground mb-2 text-xs font-bold tracking-wider uppercase">The Academic Deans</h6>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>• Dean, Graduate School</div>
                                            <div>• Dean, College of Law</div>
                                            <div>• Dean, College of Liberal Arts</div>
                                            <div>• Dean, College of Business &amp; CABA</div>
                                            <div>• Dean, College of Education</div>
                                            <div>• Dean, College of Info Technology</div>
                                            <div>• Dean, College of Geodetic Engineering</div>
                                            <div>• Dean, College of Criminology</div>
                                            <div>• Dean, College of Nursing</div>
                                            <div>• Principal, Basic Education Center</div>
                                        </div>
                                    </div>
                                </div>
                            </AccordionItem>

                            <AccordionItem title="The School Accountant">
                                <p className="text-sm">
                                    The Accountant is responsible for the keeping and maintenance of the books of accounts of the corporation. The
                                    Accountant keeps records of the funds and properties of the corporation and is in charge of the custody and
                                    safekeeping of those funds and properties.
                                </p>
                            </AccordionItem>

                            <AccordionItem title="The School Librarian">
                                <p className="text-sm">
                                    The School Librarian attends to the needs of the students in the K-12, undergraduate, graduate and law programs,
                                    relative to the use of the library. The Librarian is responsible in classifying, accessing, cataloging and
                                    indexing newly purchased books, magazines and reading materials; supervising the work of the library assistants;
                                    formulating policies regarding the use of the library; maintaining a list of all books and conducting an annual
                                    inventory of the same.
                                </p>
                            </AccordionItem>

                            <AccordionItem title="The Student Affairs Director">
                                <p className="text-sm">
                                    The Student Affairs Coordinator is responsible for coordinating the wide range of student services and
                                    non-academic activities of the school.
                                </p>
                            </AccordionItem>

                            <AccordionItem title="The Human Resources Director">
                                <ul className="list-inside list-disc space-y-2 text-sm">
                                    <li>
                                        Develops organization strategies by identifying and researching human resources issues; contributing
                                        information, analysis, and recommendations to organization strategic thinking.
                                    </li>
                                    <li>
                                        Manages human resources operations by recruiting, selecting, orienting, training, coaching, counseling, and
                                        disciplining staff.
                                    </li>
                                </ul>
                            </AccordionItem>
                        </div>

                        <div className="space-y-4">
                            <AccordionItem title="Community Involvement &amp; Extension">
                                <p className="text-sm">
                                    The Extension Service Coordinator strategizes, organizes, designs and implements all extension service activities
                                    of students in coordination with College Deans and coordinates with their respective faculty in charge to serve
                                    neighboring communities.
                                </p>
                            </AccordionItem>

                            <AccordionItem title="Academic and Administrative Councils">
                                <p className="text-sm">
                                    There shall be Academic Council and Administrative Council with the School President as Chairman of both Councils.
                                    Both Councils shall have one Vice Chairman and one Secretary who will be elected from among the members in a joint
                                    meeting called for the purpose of electing such positions.
                                </p>
                            </AccordionItem>

                            <AccordionItem title="The Academic Council Functions">
                                <div className="space-y-3 text-sm">
                                    <p>The Academic Council is the main forum for academic policy formulation of the Colleges.</p>
                                    <strong className="text-foreground mt-2 block text-xs tracking-wider uppercase">Key Areas:</strong>
                                    <ol className="list-inside list-decimal space-y-1 text-xs">
                                        <li>Policies on student admission, retention, load, and graduation requirements.</li>
                                        <li>Academic standards (attendance, grading system, academic awards, scholarship).</li>
                                        <li>Academic requirements such as research, outreach or extension.</li>
                                        <li>Curriculum development, review and or revision, coordination of course offerings.</li>
                                        <li>Policies on Faculty Loading System, performance standards of academic-teaching personnel.</li>
                                    </ol>
                                </div>
                            </AccordionItem>

                            <AccordionItem title="The Administrative Council Functions">
                                <div className="space-y-3 text-sm">
                                    <p>
                                        The Administrative Council shall have the power to recommend policies and guidelines on matters concerning the
                                        administrative operation of the school subject to the approval of the Board of Directors upon the endorsement
                                        of the President/Chairman.
                                    </p>
                                    <strong className="text-foreground block text-xs tracking-wider uppercase">Membership:</strong>
                                    <ul className="list-inside list-disc space-y-1 text-xs">
                                        <li>The School Treasurer</li>
                                        <li>The School Accountant</li>
                                        <li>The Building Superintendent</li>
                                        <li>The College Registrar</li>
                                    </ul>
                                </div>
                            </AccordionItem>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
